import { useEffect, useState } from "react";
import { subscribePresence } from "./presence";
import type { CollaboratorState } from "./sharedRoomTypes";

const EMPTY: ReadonlyMap<string, CollaboratorState> = new Map();

/**
 * Subscribe to all collaborators' RTDB presence for a shared room.
 *
 * Returns the local user's view: a `Map<uid, CollaboratorState>` keyed by
 * the *other* user's uid (the local uid is filtered out by
 * {@link import("./presence").subscribePresence}).
 *
 * Pass `null` for `roomId` or `selfUid` to disable the subscription —
 * useful when the user is in a private room or signed out. Both cases
 * return an empty map.
 */
export function useCollaborators(
  roomId: string | null,
  selfUid: string | null
): ReadonlyMap<string, CollaboratorState> {
  // Snapshot is keyed by the room it came from so switching rooms never
  // shows the previous room's cursors while the new subscription warms up.
  const [snap, setSnap] = useState<{
    roomId: string | null;
    map: ReadonlyMap<string, CollaboratorState>;
  }>({ roomId: null, map: EMPTY });

  useEffect(() => {
    if (!roomId || !selfUid) return;
    const sub = subscribePresence(roomId, selfUid, (next) => {
      // Snapshot to a fresh Map so React's reference-equality bails out
      // correctly on rerenders.
      setSnap({ roomId, map: new Map(next) });
    });
    return () => sub.unsubscribe();
  }, [roomId, selfUid]);

  if (!roomId || !selfUid) return EMPTY;
  return snap.roomId === roomId ? snap.map : EMPTY;
}
